import React, {useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useNavigate, useParams} from 'react-router';
import {usePIR} from '../../../../../firebase/firestore/usePIR';
import PIRCollectionRequests from './PIRCollectionRequests';
import Timestamp from '../../../../../components/Timestamp';
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`} : {}; }

/**
 * PIRPage - displays a single PIR document and related collections (requests, required fintel formats).
 * Route: /PIR/:pirId
 */
const PIRPage = () => {
    const {pirId} = useParams();
    const navigate = useNavigate();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const {data, loading, error} = usePIR(pirId);

    const handleDelete = async () => {
        if (!pirId) return;
        setDeleting(true);
        try {
            await fetch(`${_awpBase}/api/intelligence-requests/${encodeURIComponent(pirId)}`, {method: 'DELETE', headers: _authH()});
            setConfirmOpen(false);
            navigate('/PIR');
        } catch (err) {
            console.error('Failed to delete PIR', err);
            alert('Failed to delete PIR: ' + (err.message || err));
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return <Stack p={2}>Loading PIR…</Stack>;
    if (error) return <Stack p={2} sx={{color: 'red'}}>Error loading PIR: {String(error?.message || error)}</Stack>;
    if (!data) return <Stack p={2}>PIR not found.</Stack>;

    return (
        <Stack>
            <Stack p={1} bgcolor={'white'} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Button onClick={() => navigate('/PIR')}>Back</Button>
                <Button
                    variant="text"
                    onClick={() => setConfirmOpen(true)}
                    startIcon={<DeleteOutlineIcon/>}
                    color="error"
                >
                    Delete
                </Button>
            </Stack>

            <Stack direction={'row'} p={2} spacing={2} alignItems={'flex-start'}>
                <Stack p={2} borderRadius={2} bgcolor={'white'} spacing={2} flex={1}>
                    <Stack spacing={1}>
                        <Typography variant={'h5'}>{data.pirId || data.id}</Typography>
                        <Typography variant={'subtitle1'}>{data.mainQuestion || data.title}</Typography>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant={'body2'} color={'text.secondary'}>Details:</Typography>
                        <Typography sx={{whiteSpace: 'pre-line'}}>{data.description}</Typography>
                    </Stack>

                    <Divider/>

                    <PIRCollectionRequests pirId={data.id || data.pirId}/>

                    <Divider/>

                    <Stack>
                        <Typography variant={'h6'}>Required Fintel Formats</Typography>
                        {/* Placeholder: render expected fintel formats from PIR document if present */}
                        {data.required_fintel_formats && data.required_fintel_formats.length > 0 ? (
                            data.required_fintel_formats.map((f, i) => (
                                <Typography key={i} sx={{mt: 1}}>- {f}</Typography>
                            ))
                        ) : (
                            <Typography sx={{mt: 1}}>No fintel formats specified.</Typography>
                        )}
                    </Stack>
                </Stack>

                <Stack p={2} borderRadius={2} bgcolor={'white'} width={320} spacing={2}>
                    <Typography variant={'h6'}>Metadata</Typography>
                    <Stack spacing={1}>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Created by:</Typography>
                            <Typography>{data.createdBy}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Created at:</Typography>
                            <Timestamp component={Typography} value={data.createdAt} fallback={'N/A'}/>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Assigned to:</Typography>
                            <Typography>{data.assignedTo || 'N/A'}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Parent IR:</Typography>
                            <Typography>{data.parent_ir || 'N/A'}</Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            This action will permanently delete PIR "{data?.pirId || data?.id}".
                        </Typography>
                        <Alert severity={'warning'}>This cannot be undone.</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default PIRPage;